import { useEffect, useMemo, useRef } from 'react'

import {
  OneCDiagnosticsPanel,
  makeSelectOneCDiagnosticsVm,
  onecConnectionCheck,
  onecProfile,
  type OneCDiagnosticsActions,
  type OneCResourceArgs,
} from '@krp/resource-onec-ui'

import { useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { OneCWebhookBinding } from './OneCWebhookBinding'

/**
 * Binding панели диагностики базы 1С в карточке ресурса Platform: rid приходит
 * явно от окна карточки, а не из глобального выбора. Проверка связи — только по
 * кнопке (одна проверка = один запрос службы к 1С).
 */
export function OneCDiagnosticsBinding({ resourceId }: { resourceId: string }) {
  const args = useMemo<OneCResourceArgs>(() => ({ resourceId }), [resourceId])
  const { refetch } = onecProfile.useQuerySubscription(args)
  const [check, checkState] = onecConnectionCheck.useMutation()

  const selectProfile = useMemo(() => onecProfile.select(args), [args])
  const profile = useAppSelector((state: RootState) => selectProfile(state))
  // Итог проверки относится к ревизии учётных данных: новая ревизия (смена пароля, адреса) — старый итог снимается.
  const revision = profile.data?.configurationVersion
  const checkedRevision = useRef(revision)
  const resetCheck = checkState.reset
  useEffect(() => {
    if (revision === undefined || checkedRevision.current === revision) return
    checkedRevision.current = revision
    resetCheck()
  }, [resetCheck, revision])

  const selectVm = useMemo(() => makeSelectOneCDiagnosticsVm(), [])
  const vm = selectVm({ profile, check: checkState })

  const actions = useMemo<OneCDiagnosticsActions>(
    () => ({
      reload: () => void refetch(),
      check: () => void check(args),
    }),
    [args, check, refetch],
  )
  return (
    <>
      <OneCDiagnosticsPanel vm={vm} actions={actions} />
      <OneCWebhookBinding resourceId={resourceId} />
    </>
  )
}
